import { zustandStorage } from "@helpers/storage";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

// Tracks the live session per ProEthica module (keyed by the dashboard tag id
// stored as currentModuleId). Practice tests are once-only: on re-entry we
// resume this tracked session instead of creating a new one, and the module list
// reflects its status (ACTIVE -> "Resume", COMPLETE -> "View Report"). Persisted
// so it survives reload.
export type ModuleSessionStatus = "ACTIVE" | "COMPLETE";
export type ModuleSession = {
	numericId: number;
	status: ModuleSessionStatus;
};

type State = {
	sessionByModule: Record<string, ModuleSession>;
	setModuleSession: (moduleId: string, session: ModuleSession) => void;
	clearModuleSession: (moduleId: string) => void;
	// Pre-test completion (submitted, any score) and post-test pass (>= 80%) drive
	// review-unlock and, under the live flow, the module/post-test gating model.
	preTestCompleted: boolean;
	postTestPassed: boolean;
	setPreTestCompleted: (completed: boolean) => void;
	setPostTestPassed: (passed: boolean) => void;
};

export const useProethicaSessionStore = create<State>()(
	persist(
		(set) => ({
			sessionByModule: {},
			preTestCompleted: false,
			postTestPassed: false,
			setModuleSession: (moduleId, session) =>
				set((state) => ({
					sessionByModule: {
						...state.sessionByModule,
						[moduleId]: session,
					},
				})),
			clearModuleSession: (moduleId) =>
				set((state) => {
					const next = { ...state.sessionByModule };
					delete next[moduleId];
					return { sessionByModule: next };
				}),
			setPreTestCompleted: (preTestCompleted) => set({ preTestCompleted }),
			setPostTestPassed: (postTestPassed) => set({ postTestPassed }),
		}),
		{
			name: "proethicaSessionStore",
			storage: createJSONStorage(() => zustandStorage),
		},
	),
);
